import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { useTranslate } from '@growtherapy/i18n';
import { Body, Button, Card, CollapseGroup, Subhead, Tag } from '@growtherapy/sprout-ui';

import {
  ClientInformationDrawerTab,
  MeasureInsightTableConnected,
  useSetActiveClientInfoDrawerTab,
} from '@grow-therapy-team/seedling-components';

import { Maybe } from '../../types';
import { useOpenClientInfoDrawerForAppointment } from '../client-information/useOpenClientInfoDrawerForAppointment';
import { providerShortIdAtom } from '../state';
import { CoachInteractionsTable } from './CoachInteractionsTable';
import { IntersessionSummaryDisclaimer } from './IntersessionSummaryDisclaimer';
import { NoUpcomingAppointmentsCard } from './NoUpcomingAppointmentsCard';
import { ResourcesTable } from './ResourcesTable';
import {
  IntersessionSummary__Appointment,
  IntersessionSummary__FollowUpPreVisitSummaryContent,
  IntersessionSummary__IntakePreVisitSummaryContent,
  IntersessionSummary__Patient,
} from './types';
import { useGetCoachAccess } from './useGetCoachAccess';
import { useIntersessionSummary } from './useGetIntersessionSummary';
import {
  formatBetweenSessionAction,
  formatFollowupHeader,
  formatFollowupMeasureTitle,
  formatIntakeHeader,
  formatIntakeMeasureTitle,
} from './utils';

function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return <BaseErrorBoundary FallbackComponent={() => null}>{children}</BaseErrorBoundary>;
}

function formatFocusOfLastSession(
  t: TFunction,
  content:
    | Maybe<IntersessionSummary__IntakePreVisitSummaryContent | IntersessionSummary__FollowUpPreVisitSummaryContent>
    | undefined,
  onClick: () => void,
): { title: string; content: JSX.Element } {
  if (!content || content.__typename === 'IntakePreVisitSummaryContentObject') {
    return {
      title: t('telehealth-core:providerIntersessionSummary.na'),
      content: (
        <Button use='link' onClick={onClick}>
          {t('telehealth-core:providerIntersessionSummary.viewPreviousNotes')}
        </Button>
      ),
    };
  }

  const focusOfLastSection = content.progressNote?.find((note) => note.label === 'Focus of the session');
  if (!focusOfLastSection || !focusOfLastSection?.item) {
    return {
      title: t('telehealth-core:providerIntersessionSummary.noNotesFromLastSession'),
      content: (
        <Button use='link' onClick={onClick}>
          {t('telehealth-core:providerIntersessionSummary.viewPreviousNotes')}
        </Button>
      ),
    };
  }

  return {
    title: focusOfLastSection.item,
    content: (
      <div className='flex flex-col rebrand:gap-350'>
        <Body.Default>{focusOfLastSection.item}</Body.Default>
        <Button use='link' onClick={onClick}>
          {t('telehealth-core:providerIntersessionSummary.viewNote')}
        </Button>
      </div>
    ),
  };
}

function IntersessionSummary({
  appointment,
  patient,
  openClientInfoDrawerForAppointment,
  providerShortId,
  hasCoachAccess,
}: {
  appointment: IntersessionSummary__Appointment;
  patient: IntersessionSummary__Patient;
  openClientInfoDrawerForAppointment: (tab: ClientInformationDrawerTab) => void;
  providerShortId: string;
  hasCoachAccess: boolean;
}): JSX.Element {
  const t = useTranslate();

  const { title: focusOfLastSessionTitle, content: focusOfLastSessionContent } = formatFocusOfLastSession(
    t,
    appointment.preVisitSummary?.content,
    () => openClientInfoDrawerForAppointment(ClientInformationDrawerTab.INFORMATION),
  );

  const pronoun = patient.pronouns?.split('/')[0] ?? 'they';
  const coachTopics = appointment.preVisitSummary?.content?.generatedCoachTopics || [];
  const completedResources =
    (appointment.preVisitSummary?.content?.__typename === 'FollowUpPreVisitSummaryContentObject'
      ? appointment.preVisitSummary?.content?.completedResources
      : []) ?? [];
  const numCompletedResources = completedResources.length;
  const reflectionTopics =
    (appointment.preVisitSummary?.content?.__typename === 'FollowUpPreVisitSummaryContentObject'
      ? appointment.preVisitSummary?.content?.reflectionTopics
      : []) ?? [];

  return (
    <Card
      title={patient.preferredFullName}
      description={format(new Date(appointment.timeStart), 'MMMM d, yyyy – h:mma')}
      headerClassName='sticky top-0 rebrand:bg-surface-default bg-neutral-200 z-10 pt-8'
      actions={hasCoachAccess && <Tag use='black'>{t('telehealth-core:providerIntersessionSummary.coachUser')}</Tag>}
      className='max-w-3xl overflow-y-scroll mb-8 sm:pt-0 pt-0 rebrand:pt-0'
    >
      <Subhead.Default>
        {appointment.preVisitSummary?.content?.__typename === 'FollowUpPreVisitSummaryContentObject'
          ? formatFollowupHeader(t, patient.firstName, pronoun, coachTopics, numCompletedResources, reflectionTopics)
          : formatIntakeHeader(t, patient.firstName, coachTopics)}
      </Subhead.Default>
      <CollapseGroup
        items={[
          {
            children: focusOfLastSessionContent,
            eyebrowText: t('telehealth-core:providerIntersessionSummary.fromLastSession'),
            title: <Subhead.Small>{focusOfLastSessionTitle}</Subhead.Small>,
            className: 'rebrand:p-0',
            expanded: true,
          },
          {
            children: (
              <div className='flex flex-col rebrand:gap-350'>
                {hasCoachAccess && <CoachInteractionsTable topics={coachTopics} />}
                {completedResources && <ResourcesTable resources={completedResources} />}
                <Button
                  use='link'
                  onClick={() => openClientInfoDrawerForAppointment(ClientInformationDrawerTab.CLIENT_ACTIVITY)}
                >
                  {t('telehealth-core:providerIntersessionSummary.viewActivity')}
                </Button>
              </div>
            ),
            eyebrowText: t('telehealth-core:providerIntersessionSummary.activitySinceLastSession'),
            title: (
              <Subhead.Small>
                {formatBetweenSessionAction(t, hasCoachAccess, coachTopics, numCompletedResources)}
              </Subhead.Small>
            ),
            className: 'rebrand:p-0',
          },
          {
            children: (
              <div className='flex flex-col rebrand:gap-350'>
                <MeasureInsightTableConnected
                  clientUserShortId={patient.clientUser.shortId}
                  providerShortId={providerShortId}
                  isProvider
                  isShownWithinPaperworkFlow={false}
                  timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
                />
                <Button
                  use='link'
                  onClick={() => openClientInfoDrawerForAppointment(ClientInformationDrawerTab.TREATMENT)}
                >
                  {t('telehealth-core:providerIntersessionSummary.viewMeasures')}
                </Button>
              </div>
            ),
            eyebrowText: t('telehealth-core:providerIntersessionSummary.measures'),
            title: (
              <Subhead.Small>
                {appointment.preVisitSummary?.content?.__typename === 'FollowUpPreVisitSummaryContentObject'
                  ? formatFollowupMeasureTitle(
                      t,
                      appointment.preVisitSummary?.content?.currentGadCategory ?? undefined,
                      appointment.preVisitSummary?.content?.gadDifference ?? undefined,
                      appointment.preVisitSummary?.content?.currentPhqCategory ?? undefined,
                      appointment.preVisitSummary?.content?.phqDifference ?? undefined,
                    )
                  : formatIntakeMeasureTitle(
                      t,
                      appointment.preVisitSummary?.content?.currentGadCategory ?? undefined,
                      appointment.preVisitSummary?.content?.currentPhqCategory ?? undefined,
                    )}
              </Subhead.Small>
            ),
            className: 'rebrand:p-0',
          },
        ]}
        variant='default'
        className='flex flex-col rebrand:gap-450 rebrand:my-450 my-8'
      />
      {appointment.preVisitSummary && (
        <IntersessionSummaryDisclaimer createdAt={appointment.preVisitSummary?.createdAt} />
      )}
    </Card>
  );
}

export function IntersessionSummaryConnected(): JSX.Element {
  const { appointment, loading } = useIntersessionSummary();
  const providerShortId = useAtomValue(providerShortIdAtom);

  const { coachReleasedToClient, loading: isCoachLoading } = useGetCoachAccess(
    appointment?.patient?.clientUser?.shortId ?? '',
    {
      skip: !appointment?.patient?.clientUser?.shortId,
    },
  );

  const openClientInfoDrawerForAppointment = useOpenClientInfoDrawerForAppointment();
  const setSeedlingActiveTab = useSetActiveClientInfoDrawerTab();

  const handleOpenDrawer = useCallback(
    (tab: ClientInformationDrawerTab) => {
      if (appointment) {
        openClientInfoDrawerForAppointment(appointment);
        setSeedlingActiveTab(tab);
      }
    },
    [appointment, openClientInfoDrawerForAppointment, setSeedlingActiveTab],
  );

  if (loading || isCoachLoading) {
    return <Card />;
  }

  if (!appointment || !providerShortId || !appointment.patient) {
    return <NoUpcomingAppointmentsCard />;
  }

  return (
    <ErrorBoundary>
      <IntersessionSummary
        appointment={appointment}
        patient={appointment.patient}
        openClientInfoDrawerForAppointment={(tab: ClientInformationDrawerTab) => handleOpenDrawer(tab)}
        providerShortId={providerShortId}
        hasCoachAccess={coachReleasedToClient ?? false}
      />
    </ErrorBoundary>
  );
}
